import {
  Maybe,
  UserRewindEntityFragmentFragment,
  UserRewindMovieFragmentFragment,
  UserRewindScreenFragmentFragment,
  UserRewindSeriesFragmentFragment
} from 'types/graphql-api.generated';

import hasTouch from 'common/tools/dom/getTouchBrowser';
import trans from 'common/tools/translations/trans';

export type ScreenLabels = Partial<Record<ScreenNames, string>>;
export type MissingScreenType = 'movie' | 'series' | 'celebrity' | 'genre';

// Device detection:
// As cell phones and tablets have limited hardware resources,
// renderings with a maximum resolution of 1080 px wide may fail.
// It is therefore necessary to adapt the resolution to 720 px wide for these devices to guarantee optimum performance.
const DPR = hasTouch() ? 1.92 : 2.88; // original layer width 375 px => (375 x 1.92) 720 px for mobile, (375 x 2.88) 1080 px for desktop
const startScreenList: ScreenNames[] = ['start_screen', 'screen_upload_data'];

const endScreenList: ScreenNames[] = [
  'movie_suggestions',
  'series_suggestions',
  'rewind_captif'
];

const slideScreenList: ScreenNames[] = [
  'screen_average_rating',
  'screen_viewing_time',
  'screen_repartition',
  'member_card',
  'top_content_intro',
  'top_content_reveal_1',
  'top_content_reveal_2',
  'top_content_reveal_3',
  'screen_top_global_movies_2025_intro',
  'screen_top_global_movies_2025_reveal_1',
  'screen_top_global_movies_2025_reveal_2',
  'screen_top_global_movies_2025_reveal_3',
  'screen_top_global_series_2025_intro',
  'screen_top_global_series_2025_reveal_1',
  'screen_top_global_series_2025_reveal_2',
  'screen_top_global_series_2025_reveal_3',
  'screen_top_theater_movies_2025',
  'screen_top_series_online_2025',
  'screen_series_comparison',
  'screen_movies_comparison',
  'screen_top_movies_2025',
  'screen_favorite_movie_actor',
  'screen_favorite_series_actor',
  'screen_favorite_movie_director',
  'screen_top10_intro',
  'screen_top10_reveal',
  'screen_top10_movies',
  'screen_top10_series',
  'screen_user_ranking',
  'screen_conclusion',
  'screen_movie_top3',
  'screen_series_top3',
  'screen_movie_top3_intro',
  'screen_series_top3_intro',
  'screen_bilan_user',
  'rewind_captif'
];

export const opeBgMapping: ScreenNames[] = [
  ...startScreenList,
  ...slideScreenList,
  ...endScreenList
];

export const fullScreenList: ScreenNames[] = [
  ...startScreenList,
  'empty_screen',
  ...slideScreenList,
  'lack_of_data',
  ...endScreenList
];

export const labelsMapping = (
  type: string,
  mapping: ScreenLabels,
  screen: ScreenNames
) => {
  mapping[screen] = `rewind.${screen}.${type}`;
  return mapping;
};

export const getEntities = (
  data?: Omit<UserRewindScreenFragmentFragment, 'data'> | null,
  identifiers?: string[]
) =>
  data?.entities?.find(entity =>
    identifiers?.includes(entity?.identifier ?? '')
  )?.entities;

const viewingTimeLevels = [
  10,
  20,
  30,
  40,
  50,

  60, // 1h
  120, // 2h
  180, // 3h
  240, // 4h
  300, // 5h
  360, // 6h
  420, // 7h
  480, // 8h
  540, // 9h

  600, // 10h
  1200, // 20h
  1800, // 30h
  2400, // 40h
  3000, // 50h
  3600, // 60h
  4200, // 70h
  4800, // 80h
  5400, // 90h

  6000, // 100h
  6600, // 110h
  7200, // 120h
  7800, // 130h
  8400, // 140h
  9000, // 150h
  9600, // 160h
  10200, // 170h
  10800, // 180h
  11400, // 190h

  12000, // 200h
  12600, // 210h
  13200, // 220h
  13800, // 230h
  14400, // 240h
  15000, // 250h
  15600, // 260h
  16200, // 270h
  16800, // 280h
  17400, // 290h

  18000, // 300h
  21000, // 350h

  24000, // 400h
  27000, // 450h

  30000, // 500h
  33000, // 550h

  36000, // 600h
  39000, // 650h

  42000, // 700h
  45000, // 750h

  48000, // 800h
  51000, // 850h

  54000, // 900h
  57000, // 950h

  60000 // 1000h
];
const maxLevel = viewingTimeLevels[viewingTimeLevels.length - 1];

export const getViewingText = (duration: number) => {
  if (duration > maxLevel) {
    return trans('rewind.viewing.more_than.60000');
  }

  const level = viewingTimeLevels.find(level => duration < level);
  return level ? trans(`rewind.viewing.less_than.${level}`) : null;
};

export const computeImgSize = (
  targetWidth: number,
  targetHeight: number,
  sourceWidth: number,
  sourceHeight: number
) => {
  const ratio = Math.min(
    targetWidth / sourceWidth,
    targetHeight / sourceHeight
  );

  return [Math.round(sourceWidth * ratio), Math.round(sourceHeight * ratio)];
};

export const getDPRSize = (size: number) => Math.round(size * DPR);
const screenChecks: Array<{
  screens: ScreenNames[];
  type: MissingScreenType;
}> = [
  {
    screens: ['screen_top10_movies', 'screen_viewing_time'],
    type: 'movie'
  },
  {
    screens: ['screen_favorite_movie_actor', 'screen_favorite_movie_director'],
    type: 'celebrity'
  },

  { screens: ['screen_repartition'], type: 'series' }
];

export const calculateScreenList = (dataList: ScreenNames[]) => {
  const missingScreensSet = new Set<MissingScreenType>([
    'movie',
    'celebrity',
    'series'
  ]);

  screenChecks.forEach(({ screens, type }) => {
    if (type === 'series') {
      if (dataList.includes('screen_repartition')) {
        missingScreensSet.delete(type);
      }
    } else if (screens.some(screen => dataList.includes(screen))) {
      missingScreensSet.delete(type);
    }
  });

  const sortedDataList = dataList
    .filter(screen => screen !== 'empty_screen')
    .sort((a, b) => opeBgMapping.indexOf(a) - opeBgMapping.indexOf(b));

  const missingScreens = Array.from(missingScreensSet);
  const result: [ScreenNames[], MissingScreenType[]] = [
    [...startScreenList],
    missingScreens
  ];

  const totalPossibleScreens = 2;
  if (missingScreens.length >= totalPossibleScreens) {
    result[0].push('empty_screen', ...endScreenList);
  } else if (missingScreens.length > 0) {
    const lastValidScreen = sortedDataList[sortedDataList.length - 1];
    const index = sortedDataList.indexOf(lastValidScreen);
    result[0] =
      index !== -1
        ? [
            ...sortedDataList.slice(0, index + 1),
            'lack_of_data',
            ...endScreenList
          ]
        : [
            ...sortedDataList.filter(screen => !endScreenList.includes(screen)),
            'lack_of_data',
            ...endScreenList
          ];
  } else {
    result[0] = [...sortedDataList];
  }

  return result;
};

export const getLimitedEntities = (
  max: number,
  entities?: Maybe<Array<Maybe<UserRewindEntityFragmentFragment>>>
) =>
  entities?.reduce<
    Array<UserRewindMovieFragmentFragment | UserRewindSeriesFragmentFragment>
  >((acc, entity) => {
    if (
      acc.length < max &&
      (entity?.__typename === 'Movie' || entity?.__typename === 'Series')
    ) {
      acc.push(entity);
    }
    return acc;
  }, []);
